/**
 * Scrolling Block - Interactivity API View Script
 *
 * Unified animation engine using the Web Animations API.
 * Handles all 4 combos (infinite/finite × startOffscreen on/off)
 * with pixel-perfect speed matching from measured dimensions.
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

/** Speed presets → loop duration in seconds (one copy's worth). */
const SPEED_MAP = {
	slow: 40,
	medium: 20,
	fast: 10,
	'very-fast': 4,
};

store( 'brandy/scrolling', {
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();

			// Respect reduced motion preference — show static content.
			if (
				window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
			) {
				return;
			}

			const track = ref.querySelector( '.brandy-scrolling__track' );
			if ( ! track ) {
				return;
			}

			// --- Measure actual dimensions ---
			const containerWidth = ref.offsetWidth;
			const trackWidth = track.scrollWidth;
			const copies = ctx.infinite ? 4 : 1;
			const copyWidth =
				copies > 1 ? trackWidth / copies : trackWidth;

			// Calculate consistent speed: px/s based on copy width and duration.
			const loopDuration = SPEED_MAP[ ctx.speed ] || 20;
			const pxPerSec = copyWidth / loopDuration;
			const isLeft = ctx.direction === 'left';

			// --- Animation helper ---
			let currentAnim = null;

			function play( from, to, opts = {} ) {
				const dist = Math.abs( to - from );
				if ( dist < 1 ) {
					return null;
				}

				const duration = ( dist / pxPerSec ) * 1000; // ms

				currentAnim = track.animate(
					[
						{ transform: `translateX(${ from }px)` },
						{ transform: `translateX(${ to }px)` },
					],
					{
						duration,
						iterations: opts.loop ? Infinity : 1,
						easing: 'linear',
						fill: opts.loop ? 'none' : 'forwards',
					}
				);

				return currentAnim;
			}

			// --- Start the right animation based on mode ---
			if ( ctx.infinite ) {
				// Infinite loop positions (seamless with 4 duplicated groups).
				const loopFrom = isLeft ? 0 : -copyWidth;
				const loopTo = isLeft ? -copyWidth : 0;

				function startLoop() {
					play( loopFrom, loopTo, { loop: true } );
				}

				if ( ctx.startOffscreen ) {
					// Entry: offscreen → loop start, then seamless loop.
					const entryFrom = isLeft
						? containerWidth
						: -( copyWidth + containerWidth );
					const entryAnim = play( entryFrom, loopFrom );

					if ( entryAnim ) {
						entryAnim.onfinish = () => {
							entryAnim.cancel();
							startLoop();
						};
					} else {
						startLoop();
					}
				} else {
					startLoop();
				}
			} else {
				// Finite mode (single group). Content scrolls through and exits.
				if ( ctx.startOffscreen ) {
					// Enter from offscreen, scroll through, exit other side.
					const from = isLeft ? containerWidth : -trackWidth;
					const to = isLeft ? -trackWidth : containerWidth;
					play( from, to );
				} else if ( trackWidth > 0 ) {
					// Start visible, scroll through until content exits.
					const from = isLeft ? 0 : -trackWidth + containerWidth;
					const to = isLeft ? -trackWidth : containerWidth;
					play( from, to );
				}
			}

			// --- Pause on hover ---
			let onEnter, onLeave;

			if ( ctx.pauseOnHover ) {
				onEnter = () => {
					if (
						currentAnim &&
						currentAnim.playState === 'running'
					) {
						currentAnim.pause();
					}
				};
				onLeave = () => {
					if (
						currentAnim &&
						currentAnim.playState === 'paused'
					) {
						currentAnim.play();
					}
				};
				ref.addEventListener( 'mouseenter', onEnter );
				ref.addEventListener( 'mouseleave', onLeave );
			}

			// --- Cleanup on destroy ---
			return () => {
				if ( currentAnim ) {
					currentAnim.cancel();
				}
				if ( onEnter ) {
					ref.removeEventListener( 'mouseenter', onEnter );
				}
				if ( onLeave ) {
					ref.removeEventListener( 'mouseleave', onLeave );
				}
			};
		},
	},
} );
